import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { subscriptionService } from "../marketplace/services/subscriptionService";

/** Normalize subscription from API (handles both { data: { subscription } } and { subscription }). */
export const getSubscriptionFromResponse = (res) =>
  res?.data?.subscription ?? res?.subscription ?? null;

/** Skip refetch if we already have data from this window (avoids duplicate calls from multiple components). */
const SUBSCRIPTION_STALE_MS = 3000;

export const fetchSubscription = createAsyncThunk(
  "subscription/fetch",
  async (_, { getState, rejectWithValue }) => {
    const { data, lastFetched } = getState().subscription ?? {};
    if (data != null && lastFetched != null && Date.now() - lastFetched < SUBSCRIPTION_STALE_MS) {
      return data;
    }
    try {
      const res = await subscriptionService.getSubscription();
      return getSubscriptionFromResponse(res);
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState: {
    data: null,
    loading: false,
    error: null,
    lastFetched: null,
  },
  reducers: {
    /** Set subscription from payload (e.g. from polling) to avoid extra API call. */
    setSubscriptionData(state, action) {
      const payload = action.payload;
      if (payload != null) {
        state.data = payload;
        state.lastFetched = Date.now();
        state.error = null;
      }
    },
    /** Update only view usage from track-view response (no full refetch). */
    updateProfileViewUsage(state, action) {
      const { viewLimit, viewsUsed, viewsRemaining } = action.payload ?? {};
      if (state.data != null && typeof viewsUsed === 'number') {
        state.data.viewLimit = viewLimit ?? state.data.viewLimit;
        state.data.viewsUsed = viewsUsed;
        state.data.viewsRemaining =
          typeof viewsRemaining === 'number' ? viewsRemaining : Math.max(0, (state.data.viewLimit ?? 0) - viewsUsed);
        state.lastFetched = Date.now();
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubscription.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.lastFetched = Date.now();
      })
      .addCase(fetchSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setSubscriptionData, updateProfileViewUsage } = subscriptionSlice.actions;

/** Redux subscription data (planType, isActive, etc.). */
export const selectSubscription = (state) => state.subscription?.data ?? null;

/** True when user has an active paid plan (source of truth for checkout UX). */
export const selectIsSubscriptionActive = (state) => {
  const sub = state.subscription?.data;
  return (
    sub != null &&
    sub.planType !== "basic" &&
    sub.isActive === true
  );
};

/** Remaining profile views for marketplace (today's window). */
export const selectViewsRemaining = (state) => {
  const sub = state.subscription?.data;
  return sub != null && typeof sub.viewsRemaining === 'number' ? sub.viewsRemaining : null;
};

/** View limit from plan for marketplace. */
export const selectViewLimit = (state) => {
  const sub = state.subscription?.data;
  return sub != null && typeof sub.viewLimit === 'number' ? sub.viewLimit : null;
};

export default subscriptionSlice.reducer;
